import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, MapPin, ArrowRight, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { Reveal, Eyebrow } from "../components/Reveal";
import { QuoteButton, BookButton } from "../components/CTAButtons";
import { CTABand } from "../components/CTABand";
import { LOCATIONS } from "../data/locations";
import { SERVICE_NAV, WARRANTY_NOTE } from "../data/services";

export default function ServicePage({ service: s }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: s.name,
    provider: { "@type": "AutoRepair", name: "My Mobile Mechanic", telephone: "+1-978-967-9860" },
    areaServed: "Middlesex County, MA & Southern New Hampshire",
    description: s.metaDesc,
  };

  return (
    <>
      <SEO title={s.metaTitle} description={s.metaDesc} path={`/${s.slug}`} jsonLd={jsonLd} />

      <section className="grain relative overflow-hidden border-b border-edge">
        <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
          <Reveal>
            <Eyebrow>Mobile Auto Repair · We Come to You</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl">
              {s.h1}
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">{s.intro}</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <QuoteButton to={`/get-a-brake-quote?service=${s.value}`} testid={`service-${s.slug}-quote-button`}>
                Get a Quote
              </QuoteButton>
              <BookButton />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-edge bg-slate2">
        <div className="mx-auto grid max-w-7xl items-start gap-10 px-4 py-14 sm:px-6 sm:py-20 lg:grid-cols-2">
          <Reveal>
            <h2 className="font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50">
              {s.signsTitle}
            </h2>
            <ul className="mt-6 space-y-3">
              {s.signs.map((sign) => (
                <li key={sign} className="flex items-center gap-3 border border-edge bg-white px-5 py-4">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber2" />
                  <span className="text-sm font-semibold text-slate-200">{sign}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <div className="space-y-6">
            {s.sections.map((sec, i) => (
              <Reveal key={sec.h2} delay={i * 0.1}>
                <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{sec.h2}</h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-400 sm:text-base">{sec.text}</p>
              </Reveal>
            ))}
            {s.warranty && (
              <Reveal delay={0.2}>
                <div className="flex items-start gap-3 border border-emerald-300 bg-emerald-50 p-5">
                  <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-600" />
                  <p className="text-sm leading-relaxed text-slate-200">
                    <strong className="text-slate-50">Warranty-backed.</strong> {WARRANTY_NOTE}
                  </p>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
        <Reveal>
          <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-50">
            <MapPin className="mr-2 inline h-6 w-6 text-crimson" />
            {s.name} Near You
          </h2>
        </Reveal>
        <Reveal className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LOCATIONS.map((l) => (
            <Link
              key={l.slug}
              to={`/${l.slug}`}
              data-testid={`service-location-${l.city.toLowerCase()}-link`}
              className="border border-edge bg-slate2 px-4 py-3 font-display text-base font-bold uppercase text-slate-200 transition-colors hover:border-crimson hover:text-crimson"
            >
              {l.city}, {l.st}
            </Link>
          ))}
        </Reveal>
        <Reveal className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-edge pt-6">
          <span className="font-mono text-[11px] uppercase tracking-widest text-slate-500">Other services:</span>
          {SERVICE_NAV.filter((n) => n.slug !== `/${s.slug}`).map((n) => (
            <Link
              key={n.slug}
              to={n.slug}
              className="flex items-center gap-1 text-sm font-semibold text-crimson hover:text-crimsondark"
            >
              {n.label} <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          ))}
        </Reveal>
        <Reveal className="mt-6 flex items-start gap-3 border border-edge bg-slate2 p-5">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber2" />
          <p className="text-sm leading-relaxed text-slate-300">
            12 years in business, 20+ years of hands-on experience — every job done where your car is parked.
          </p>
        </Reveal>
      </section>

      <CTABand title={`Need ${s.name}? Get a Quote in Two Minutes`} />
    </>
  );
}
